import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core'
import { BehaviorSubject, Subject } from 'rxjs'
import {
  DataContextBuilder,
  Filter,
  IDataContext,
  Sort,
  ElderTableComponent,
  ElderTableToolbarDirective,
  ElderDataToolbarComponent,
  ElderToolbarContentDirective,
  ElderFormFieldDenseDirective,
  ElderSelectComponent,
  ElderAutoSelectFirstDirective,
  ElderStopEventPropagationDirective,
  ElderDataViewInteractionMode,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { StorageNodeFilter, StorageNodeService } from '../storage-node.service'
import { StorageNode, StorageNodeType } from '../storage-node'
import { ActivatedRoute, ParamMap, Router } from '@angular/router'
import { StoragePath } from '../../storage-path'
import { Facility } from '../../../facilities/facility'
import { StorageBrowseOptions } from './storage-node-browse-options'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { MatBadge } from '@angular/material/badge'
import { MatTooltip } from '@angular/material/tooltip'
import {
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
} from '@angular/material/table'
import { MatInput } from '@angular/material/input'
import { FormsModule } from '@angular/forms'
import { FacilitySelectDirective } from '../../../facilities/facility-select.directive'
import { MatFormField } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatIconButton } from '@angular/material/button'
import { MatSort, MatSortHeader } from '@angular/material/sort'
import { AsyncPipe } from '@angular/common'

@Component({
  selector: 'app-storage-node-browser',
  templateUrl: './storage-node-browser.component.html',
  styleUrls: ['./storage-node-browser.component.scss'],
  imports: [
    ElderTableComponent,
    MatSort,
    ElderTableToolbarDirective,
    ElderDataToolbarComponent,
    ElderToolbarContentDirective,
    MatIconButton,
    MatIcon,
    MatFormField,
    ElderFormFieldDenseDirective,
    ElderSelectComponent,
    FacilitySelectDirective,
    ElderAutoSelectFirstDirective,
    FormsModule,
    MatInput,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    MatTooltip,
    MatBadge,
    ElderStopEventPropagationDirective,
    AsyncPipe,
    ElderTranslatePipe,
  ],
})
export class StorageNodeBrowserComponent implements OnInit, OnDestroy {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  private readonly _destroy$ = new Subject<void>()

  public readonly StorageNodeType = StorageNodeType

  public readonly browseRoot$ = new BehaviorSubject<string>('')
  public readonly browseOptions$ = new BehaviorSubject<StorageBrowseOptions>(
    StorageBrowseOptions.defaultOptions()
  )

  public readonly data: IDataContext<StorageNode>

  public facilityId: string

  @Input()
  public canAdd = false

  @Input()
  public interactionMode: ElderDataViewInteractionMode = 'open'

  @Input()
  public selectionVisible = false

  @Output()
  public nodeClick = new EventEmitter<StorageNode>()

  @Output()
  public selectionSingleChange = new EventEmitter<StorageNode>()

  @Output()
  public newNode = new EventEmitter<StoragePath>()

  @Output()
  public pathChange = new EventEmitter<StoragePath>()

  private router = inject(Router)
  private route = inject(ActivatedRoute)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const storageNodeService = inject(StorageNodeService)
    this.data = DataContextBuilder.start<StorageNode>()
      .pageSize(25)
      .reloadOnLocalChanges()
      .build(storageNodeService)
  }

  /***************************************************************************
   *                                                                         *
   * Lifecycle                                                               *
   *                                                                         *
   **************************************************************************/

  public ngOnInit(): void {
    const queryParamsMap = this.route.snapshot.queryParamMap
    this.initBrowserFromQueryParams(queryParamsMap)
  }

  public ngOnDestroy(): void {
    this._destroy$.next()
    this._destroy$.complete()

    if (this.data) {
      this.data.close()
    }

    this.cleanUpQueryParams()
  }

  private initBrowserFromQueryParams(queryParamsMap: ParamMap): void {
    const pathStr = queryParamsMap.get('path')
    const selectionUpperLimitPathStr = queryParamsMap.get('selectionUpperLimitPath')
    const browsableFacilitiesStr = queryParamsMap.get('browsableFacilities')

    this.updateOptions((options) => {
      if (selectionUpperLimitPathStr) {
        this.log.debug('ngOnInit: setting selectionUpperLimitPath: ', selectionUpperLimitPathStr)
        options = options.withSelectionUpperLimitPath(selectionUpperLimitPathStr)
      }

      if (browsableFacilitiesStr) {
        const browsableFacilities = browsableFacilitiesStr
          .split(',')
          .map((facilityId) => facilityId.toLowerCase())

        if (browsableFacilities) {
          this.log.debug('ngOnInit: setting browsableFacilities: ', browsableFacilities)
          options = options.withBrowsableFacilities(browsableFacilities)
        }
      }

      return options
    })

    if (pathStr) {
      this.log.debug('ngOnInit: setting path to ', pathStr)
      this.onBrowsePathChange(pathStr)
    }
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  public get browsePath(): string {
    return this.browseOptions.browsePath
  }

  @Input()
  public set browseOptions(options: StorageBrowseOptions) {
    if (options == null) {
      options = StorageBrowseOptions.defaultOptions()
    }
    this.onBrowsePathChange(options.browsePath)
    this.browseOptions$.next(options)
  }

  public get browseOptions(): StorageBrowseOptions {
    return this.browseOptions$.getValue()
  }

  /***************************************************************************
   *                                                                         *
   * Functions                                                               *
   *                                                                         *
   **************************************************************************/

  public readonly facilityDisplayResolverFn = (facility: Facility) => {
    return facility ? facility.id.toUpperCase() : null
  }

  public readonly isFacilityDisabledFn = (facility: Facility) => {
    return this.browseOptions.browsableFacilities
      ? !this.browseOptions.browsableFacilities.includes(facility.id)
      : false
  }

  public readonly isSelectableFn = (node: StorageNode) => {
    return this.isSelectableByNodeIdFn(node) && this.isSelectableByPathLimitFn(node)
  }

  public readonly isSelectableByNodeIdFn = (node: StorageNode) => {
    return this.browseOptions.selectableNodeIds
      ? this.browseOptions.selectableNodeIds.includes(node.id)
      : true
  }

  public readonly isSelectableByPathLimitFn = (node: StorageNode) => {
    return this.browseOptions.selectionUpperLimitPath
      ? StoragePath.parse(node.path).isSubPathOfPathString(
          this.browseOptions.selectionUpperLimitPath
        )
      : true
  }

  /***************************************************************************
   *                                                                         *
   * Component Events                                                        *
   *                                                                         *
   **************************************************************************/

  /**
   * Occurs when the bread crumb component path has changed
   */
  public onBreadCrumbPathChanged(newPath: string): void {
    this.onBrowsePathChange(newPath)
  }

  public onFacilityIdUpdated(newFacilityId: string): void {
    this.onBrowsePathChange(newFacilityId.toUpperCase())
  }

  /**
   * Occurs when the node navigation button is clicked.
   */
  public onNavigateToNode(node: StorageNode): void {
    this.onBrowsePathChange(node.path)
  }

  public canNavigateBack(): boolean {
    if (this.browsePath) {
      return StoragePath.parse(this.browsePath).parentPath.level > 0
    } else {
      return false
    }
  }

  public onNavigateBack(): void {
    if (this.browsePath) {
      const currentPath = StoragePath.parse(this.browsePath)
      const parentPath = currentPath.parentPath
      this.onBrowsePathChange(parentPath.serialize())
    }
  }

  /**
   * Occurs when a node in the browser is clicked.
   */
  public onNodeClick(node: StorageNode): void {
    if (node) {
      this.nodeClick.next(node)
    }
  }

  /**
   * Occurs when new node button was clicked.
   */
  public onNewNode(event: Event): void {
    this.newNode.emit(StoragePath.parse(this.browsePath))
  }

  public onSingleSelection(node: StorageNode): void {
    this.selectionSingleChange.next(node)
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public updateOptions(optionsFn: (opt: StorageBrowseOptions) => StorageBrowseOptions): void {
    this.browseOptions$.next(optionsFn(this.browseOptions$.getValue()))
  }

  /***************************************************************************
   *                                                                         *
   * Private methods                                                         *
   *                                                                         *
   **************************************************************************/

  private onBrowsePathChange(updatedBrowsePath: string): void {
    if (updatedBrowsePath) {
      updatedBrowsePath = updatedBrowsePath.toUpperCase()

      if (updatedBrowsePath !== this.browsePath) {
        this.updateFacility(StoragePath.parse(updatedBrowsePath).facilityId)
        this.handleBrowsePathChange(updatedBrowsePath)
      }
    }
  }

  private updateFacility(newFacilityId: string) {
    if (this.facilityId !== newFacilityId) {
      this.facilityId = newFacilityId
      this.browseRoot$.next(newFacilityId.toUpperCase())
    }
  }

  private handleBrowsePathChange(updatedPath: string) {
    updatedPath = updatedPath.toUpperCase()
    if (!this.isAllowedTarget(updatedPath)) {
      this.log.warn(
        'Navigation to path ' +
          updatedPath +
          ' not allowed. Ignoring path change. ' +
          ' Max level ' +
          this.browseOptions.maxLevelAllowed +
          ' Min level ' +
          this.browseOptions.minLevelAllowed
      )
      return
    }

    if (this.browsePath !== updatedPath) {
      this.updatePathQueryParam(updatedPath)
      this.updateOptions((options) => options.withBrowsePath(updatedPath))
      this.pathChange.emit(StoragePath.parse(updatedPath))
    }
    if (updatedPath) {
      const parts = updatedPath.split('/')
      this.updateFilters([StorageNodeFilter.parentPath(parts)])
    }
  }

  private isAllowedTarget(path: string): boolean {
    if (!path) {
      return false
    }

    return this.isAllowedTargetLevel(StoragePath.parse(path).level)
  }

  private isAllowedTargetLevel(targetLevel: number): boolean {
    if (
      this.browseOptions.minLevelAllowed != null &&
      targetLevel < this.browseOptions.minLevelAllowed
    ) {
      return false
    } else if (
      this.browseOptions.maxLevelAllowed != null &&
      targetLevel > this.browseOptions.maxLevelAllowed
    ) {
      return false
    }
    return true
  }

  private updateFilters(filters: Filter[]): void {
    if (filters && filters.filter((f) => f.key === StorageNodeFilter.PARENT_PATH).length > 0) {
      if (this.data.isStarted) {
        this.data.filter.updateFilters(filters)
      } else {
        this.data.start([new Sort('number', 'asc')], filters)
      }
    }
  }

  private updatePathQueryParam(path: string): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { path: path },
      replaceUrl: true, // if true, won't add a new browser history entry
      queryParamsHandling: 'merge',
    })
  }

  private cleanUpQueryParams() {
    this.router.navigate([], { queryParams: { path: null }, queryParamsHandling: 'merge' })
  }
}
